/* ==========================================================================
   Accessibility remediation — enCore Guards
   Runtime layer for the ADA / WCAG 2.1 AA audit findings (2026-08-18).

   This file exists because several findings live in markup the theme does not
   own: the qikify "tmenu" mega-menu (C7), the Rebuy cart flyout (S3/M4), the
   Klaviyo form (S5), an app-injected skip link (S6), the Hiver chat iframe (S8)
   and the AdRoll consent launcher (S9). Those are patched here at runtime.
   Findings in theme-owned markup are fixed in the Liquid itself.

   Everything below is idempotent and defensive: a missing element is a no-op.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function $$(sel, root) {
    try { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
    catch (e) { return []; }
  }
  function text(el) { return (el && (el.innerText || el.textContent) || '').replace(/\s+/g, ' ').trim(); }

  // Text that actually reaches the accessibility tree. Plain textContent is not
  // good enough here: the icon buttons wrap an aria-hidden <svg> that carries a
  // <title>icon-chevron</title>, which looks like a name but is never announced.
  function exposedText(el) {
    if (!el) return '';
    var out = '';
    var walk = function (node) {
      for (var i = 0; i < node.childNodes.length; i++) {
        var child = node.childNodes[i];
        if (child.nodeType === 3) { out += ' ' + child.nodeValue; continue; }
        if (child.nodeType !== 1) continue;
        if (child.getAttribute && child.getAttribute('aria-hidden') === 'true') continue;
        var tag = (child.tagName || '').toLowerCase();
        if (tag === 'svg' || tag === 'title' || tag === 'script' || tag === 'style') continue;
        if (child.getAttribute && child.getAttribute('alt')) out += ' ' + child.getAttribute('alt');
        walk(child);
      }
    };
    try { walk(el); } catch (e) { return text(el); }
    return out.replace(/\s+/g, ' ').trim();
  }
  function once(el, key) {
    if (!el || el.dataset[key] === '1') return false;
    el.dataset[key] = '1';
    return true;
  }

  /* ------------------------------------------------------------------ M3/M4/S10
     WCAG 4.1.3 Status Messages.
     One polite live region for the whole page. Variant changes, price changes
     and cart updates all announced nothing at all before this.
     ------------------------------------------------------------------------- */
  var announcer;
  function getAnnouncer() {
    if (announcer && document.body.contains(announcer)) return announcer;
    announcer = document.getElementById('a11y-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'a11y-announcer';
      announcer.className = 'a11y-visually-hidden';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      document.body.appendChild(announcer);
    }
    return announcer;
  }
  var lastMessage = '';
  function announce(msg) {
    if (!msg || msg === lastMessage) return;
    lastMessage = msg;
    var node = getAnnouncer();
    // Clear first so identical consecutive messages are still spoken.
    node.textContent = '';
    window.setTimeout(function () { node.textContent = msg; }, 60);
    window.setTimeout(function () { if (lastMessage === msg) lastMessage = ''; }, 1500);
  }

  /* ------------------------------------------------------------------------ C3
     WCAG 2.2.2 Pause, Stop, Hide.
     Seven regions animate on an infinite loop with no control of any kind.
     Wire up the footer control that pauses every animation on the page. The
     choice is remembered across pages and visits.
     ------------------------------------------------------------------------- */
  function initMotionToggle() {
    /* The control is server-rendered in the footer (sections/footer.liquid) and
       ships with the `hidden` attribute, so it never appears as a dead control
       when this script does not run. Reveal it only once it is wired up. */
    var btn = document.querySelector('[data-a11y-motion-toggle]');
    if (!btn || btn.dataset.a11yMotionBound === '1') return;

    var label = btn.querySelector('[data-a11y-motion-label]') || btn;
    var paused = false;
    try { paused = window.localStorage.getItem('a11y-motion-paused') === '1'; } catch (e) {}

    function render() {
      document.documentElement.classList.toggle('a11y-motion-paused', paused);
      btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      label.textContent = paused ? 'Resume animations' : 'Pause animations';
    }

    btn.addEventListener('click', function () {
      paused = !paused;
      try { window.localStorage.setItem('a11y-motion-paused', paused ? '1' : '0'); } catch (e) {}
      render();
      announce(paused ? 'Animations paused' : 'Animations resumed');
    });

    btn.dataset.a11yMotionBound = '1';
    render();
    btn.hidden = false;
  }

  /* ------------------------------------------------------------------------ C7
     WCAG 4.1.2 Name, Role, Value.
     Three qikify mega-menu items ship aria-expanded="true" while their submenu
     sits at zero height, and none of the 22 triggers carry aria-controls.
     Report the real state and keep it in sync.
     ------------------------------------------------------------------------- */
  function submenuOf(trigger) {
    var id = trigger.getAttribute('aria-controls');
    if (id) {
      var byId = document.getElementById(id);
      if (byId) return byId;
    }
    var parent = trigger.parentElement;
    if (!parent) return null;
    return parent.querySelector('.tmenu_submenu,.tmenu_dropdown,ul,[class*="submenu"],[class*="dropdown"],[class*="mega"]');
  }
  function isOpen(panel) {
    if (!panel) return false;
    var r = panel.getBoundingClientRect();
    if (r.height === 0 || r.width === 0) return false;
    var cs = window.getComputedStyle(panel);
    return cs.visibility !== 'hidden' && cs.display !== 'none' && parseFloat(cs.opacity) > 0.01;
  }
  function syncExpanded() {
    $$('[aria-expanded]').forEach(function (trigger) {
      // One awkward element must not abort the whole sweep.
      try {
        // Leave the drawer open/close buttons and the FAQ toggles alone: the
        // theme already keeps those in sync. Note this deliberately does NOT
        // exclude everything inside a drawer — the offending mega-menu items
        // live inside #NavDrawer (the qikify mobile menu).
        if (trigger.matches('[class*="js-drawer-open"],.js-drawer-close,[class*="faq-toggle"]')) return;
        var panel = submenuOf(trigger);
        if (!panel) return;
        if (panel.id && trigger.getAttribute('aria-controls') !== panel.id) {
          trigger.setAttribute('aria-controls', panel.id);
        } else if (!panel.id) {
          var generated = 'a11y-submenu-' + Math.random().toString(36).slice(2, 9);
          panel.id = generated;
          trigger.setAttribute('aria-controls', generated);
        }
        var open = isOpen(panel) ? 'true' : 'false';
        if (trigger.getAttribute('aria-expanded') !== open) {
          trigger.setAttribute('aria-expanded', open);
        }
      } catch (e) { /* keep going */ }
    });
  }

  /* ------------------------------------------------------------------------ C8
     WCAG 4.1.2 / 2.4.3.
     Silent tab stops: Flickity puts tabindex="0" on bare container divs, the
     promotional scrolling strip is focusable but does nothing, and the product
     gallery arrows are real buttons with no accessible name.
     ------------------------------------------------------------------------- */
  function fixSilentTabStops() {
    // Non-interactive decorative strips and carousel viewports.
    $$('div[tabindex="0"],span[tabindex="0"]').forEach(function (el) {
      if (el.getAttribute('role')) return;
      if (el.querySelector(FOCUSABLE)) {
        // It contains real controls; the wrapper itself does not need a stop.
        el.removeAttribute('tabindex');
        return;
      }
      if (!text(el) || /scrolling-text__inner|flickity-viewport|flickity-slider/.test(el.className)) {
        el.removeAttribute('tabindex');
      }
    });

    // Unnamed icon buttons, most visibly the gallery next/previous arrows.
    $$('button,[role="button"]').forEach(function (btn) {
      if (exposedText(btn)) return;
      if (btn.getAttribute('aria-label') || btn.getAttribute('aria-labelledby') || btn.getAttribute('title')) return;
      var cls = (btn.className || '') + ' ' + (btn.id || '');
      var label = '';
      if (/next/i.test(cls)) label = 'Next image';
      else if (/prev|previous/i.test(cls)) label = 'Previous image';
      else if (/close/i.test(cls)) label = 'Close';
      else if (/search/i.test(cls)) label = 'Search';
      else if (/menu|hamburger|burger/i.test(cls)) label = 'Menu';
      else if (/cart/i.test(cls)) label = 'Cart';
      else if (/play/i.test(cls)) label = 'Play video';
      else if (/pause/i.test(cls)) label = 'Pause';
      if (label) btn.setAttribute('aria-label', label);
    });

    // Unnamed links: image-only promo tiles and mega-menu image cards, where the
    // image carries no alt so the link reaches the tab order with no name at all.
    $$('a[href]').forEach(function (a) {
      if (exposedText(a)) return;
      if (a.getAttribute('aria-label') || a.getAttribute('aria-labelledby')) return;
      var titleAttr = (a.getAttribute('title') || '').trim();
      if (titleAttr) { a.setAttribute('aria-label', titleAttr); return; }
      var img = a.querySelector('img[alt]');
      var alt = img && (img.getAttribute('alt') || '').trim();
      if (alt) { a.setAttribute('aria-label', alt); return; }
      // Last resort: derive something meaningful from the destination.
      var href = (a.getAttribute('href') || '').split(/[?#]/)[0].replace(/\/$/, '');
      var slug = (href.split('/').pop() || '').replace(/[-_]+/g, ' ').trim();
      if (!slug || /^javascript:?$/i.test(href)) return;
      a.setAttribute('aria-label', slug.charAt(0).toUpperCase() + slug.slice(1));
    });

    // Wrappers that duplicate a control they already contain: the subscription
    // widget marks a <div role="radio" tabindex="0"> around a real radio input,
    // which nests one interactive control inside another.
    $$('[role="radio"],[role="checkbox"],[role="button"]').forEach(function (el) {
      if (el.matches('input,button,a,select,textarea')) return;
      if (!el.querySelector('input,button,a[href],select,textarea')) return;
      el.removeAttribute('role');
      el.removeAttribute('tabindex');
      el.removeAttribute('aria-checked');
    });

    // Flickity's own arrows use an SVG with no text.
    $$('.flickity-button.next,.flickity-prev-next-button.next').forEach(function (b) {
      if (!b.getAttribute('aria-label')) b.setAttribute('aria-label', 'Next slide');
    });
    $$('.flickity-button.previous,.flickity-prev-next-button.previous').forEach(function (b) {
      if (!b.getAttribute('aria-label')) b.setAttribute('aria-label', 'Previous slide');
    });
  }

  /* ------------------------------------------------------------------------ S3
     WCAG 4.1.2 / 1.3.1.
     The theme's drawers and the Rebuy cart flyout are div elements with a real
     focus trap but no dialog semantics, so nobody is told an overlay opened.
     ------------------------------------------------------------------------- */
  var DIALOGS = [
    { sel: '#CartDrawer', label: 'Your cart', openWhen: function (el) { return el.classList.contains('drawer--is-open'); } },
    { sel: '#NavDrawer', label: 'Menu', openWhen: function (el) { return el.classList.contains('drawer--is-open'); } },
    { sel: '.rebuy-cart__flyout', label: 'Your cart', openWhen: function (el) {
        var r = el.getBoundingClientRect();
        return r.width > 0 && r.left < window.innerWidth - 10;
      } }
  ];

  function markDialogs() {
    DIALOGS.forEach(function (d) {
      $$(d.sel).forEach(function (el) {
        if (el.getAttribute('role') !== 'dialog') el.setAttribute('role', 'dialog');
        if (el.getAttribute('aria-modal') !== 'true') el.setAttribute('aria-modal', 'true');
        if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
          var heading = el.querySelector('.drawer__title,.rebuy-cart__title,h1,h2');
          if (heading && text(heading)) {
            if (!heading.id) heading.id = 'a11y-dialog-title-' + Math.random().toString(36).slice(2, 9);
            el.setAttribute('aria-labelledby', heading.id);
          } else {
            el.setAttribute('aria-label', d.label);
          }
        }
      });
    });
  }

  // Hide the page behind an open dialog, so browse mode cannot wander into it.
  function syncDialogInertness() {
    var openDialog = null;
    DIALOGS.forEach(function (d) {
      $$(d.sel).forEach(function (el) {
        if (!openDialog && d.openWhen(el)) openDialog = el;
      });
    });
    var main = document.getElementById('MainContent');
    var header = document.querySelector('.site-header,header');
    var footer = document.querySelector('.site-footer,footer');
    [main, header, footer].forEach(function (region) {
      if (!region) return;
      if (openDialog && !region.contains(openDialog)) {
        if (region.getAttribute('aria-hidden') !== 'true') {
          region.setAttribute('aria-hidden', 'true');
          region.dataset.a11yHidden = '1';
        }
      } else if (region.dataset.a11yHidden === '1') {
        region.removeAttribute('aria-hidden');
        delete region.dataset.a11yHidden;
      }
    });
    // Announce cart totals when the cart dialog opens.
    if (openDialog && openDialog !== syncDialogInertness._last) {
      syncDialogInertness._last = openDialog;
      var label = openDialog.getAttribute('aria-label') || text(openDialog.querySelector('.drawer__title,.rebuy-cart__title')) || 'Dialog';
      announce(label + ' opened');
    } else if (!openDialog) {
      syncDialogInertness._last = null;
    }
  }

  /* ------------------------------------------------------------------------ S5
     WCAG 1.3.1 / 2.4.6.
     The Klaviyo signup form injects a second <h1> ("Sign up & save"), so
     jumping to the main heading can land in a mailing-list box. ARIA can
     re-level it without touching the app's markup.
     ------------------------------------------------------------------------- */
  function demoteInjectedHeadings() {
    var h1s = $$('h1');
    if (h1s.length < 2) return;
    h1s.forEach(function (h) {
      var injected = h.closest('[class*="klaviyo"],[class*="kl-private"],[class*="needsclick"]');
      if (!injected) return;
      if (h.getAttribute('aria-level') === '2') return;
      h.setAttribute('role', 'heading');
      h.setAttribute('aria-level', '2');
    });
  }

  /* ------------------------------------------------------------------------ S6
     WCAG 2.4.1.
     An app injects a second "Skip to main content" at tab stop 4, competing
     with the theme's own at stop 1. Keep the first, remove the rest outright:
     a skip link left in the DOM but unreachable is reported as a broken one.
     ------------------------------------------------------------------------- */
  function dedupeSkipLinks() {
    var skips = $$('a[href^="#"]').filter(function (a) { return /^skip\b/i.test(text(a)); });
    if (skips.length < 2) return;
    skips.slice(1).forEach(function (a) {
      if (a.parentNode) a.parentNode.removeChild(a);
    });
  }

  /* --------------------------------------------------------------------- 1.1.1
     WCAG 1.1.1 Non-text Content.
     The qikify menu's dropdown chevrons are bare <svg>s. Their wrapper is
     already aria-hidden, but checkers test each icon on its own, so mark the
     icon itself as decorative too.
     ------------------------------------------------------------------------- */
  function hideMenuChevrons() {
    $$('.tmenu_indicator svg:not([aria-hidden])').forEach(function (svg) {
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    });
  }

  /* ------------------------------------------------------------------------ C2
     WCAG 2.4.1 — make the surviving skip link actually move focus.
     <main> now carries tabindex="-1"; browsers still need a nudge in some cases.
     ------------------------------------------------------------------------- */
  function wireSkipLink() {
    $$('a[href^="#"]').forEach(function (a) {
      if (!/^skip\b/i.test(text(a))) return;
      if (!once(a, 'a11ySkip')) return;
      a.addEventListener('click', function (evt) {
        var id = decodeURIComponent((a.getAttribute('href') || '').slice(1));
        var target = id && document.getElementById(id);
        if (!target) return;
        evt.preventDefault();
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: false });
        if (document.activeElement !== target) {
          // Some browsers refuse focus mid-transition; try once more.
          window.setTimeout(function () { target.focus(); }, 0);
        }
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', '#' + id);
        }
      });
    });
  }

  /* ------------------------------------------------------------------------ S7
     WCAG 2.4.4 Link Purpose.
     FAQ answers link with the words "here" and "reach out", which mean nothing
     in a screen reader's list-of-links view. Give them a full accessible name
     without changing the visible marketing copy.
     ------------------------------------------------------------------------- */
  var VAGUE = /^(here|this|read more|learn more|click here|reach out|more|link)$/i;
  function nameVagueLinks() {
    $$('a[href]').forEach(function (a) {
      var t = text(a);
      if (!t || !VAGUE.test(t)) return;
      if (a.getAttribute('aria-label')) return;
      var href = a.getAttribute('href') || '';
      var slug = (href.split(/[?#]/)[0].replace(/\/$/, '').split('/').pop() || '').replace(/[-_]+/g, ' ').trim();
      if (!slug) return;
      // "here" -> "here: refund policy"
      a.setAttribute('aria-label', t + ': ' + slug);
    });
  }

  /* ------------------------------------------------------------------------ S8
     WCAG 4.1.2 — the Hiver/Chatwoot chat iframe has no title. It is 0x0 while
     closed but becomes a full interactive panel once opened.
     ------------------------------------------------------------------------- */
  var IFRAME_TITLES = [
    [/chatwoot|hiver|chat-widget/i, 'Customer support chat'],
    [/klaviyo/i, 'Email signup form'],
    [/youtube|vimeo/i, 'Video player'],
    [/recaptcha/i, 'reCAPTCHA verification'],
    [/smile|loyalty|rewards/i, 'Rewards and loyalty widget']
  ];
  function titleIframes() {
    $$('iframe').forEach(function (f) {
      if (f.getAttribute('title')) return;
      if (f.getAttribute('aria-hidden') === 'true') return;
      var hay = (f.src || '') + ' ' + (f.id || '') + ' ' + (f.className || '');
      // Tracking pixels carry no interactive content: hide rather than name.
      if (/web-pixel|pixel|analytics|doubleclick|facebook|adroll/i.test(hay)) {
        f.setAttribute('aria-hidden', 'true');
        f.setAttribute('tabindex', '-1');
        return;
      }
      for (var i = 0; i < IFRAME_TITLES.length; i++) {
        if (IFRAME_TITLES[i][0].test(hay)) { f.setAttribute('title', IFRAME_TITLES[i][1]); return; }
      }
      f.setAttribute('title', 'Embedded content');
    });
  }

  /* ------------------------------------------------------------------------ S9
     WCAG 4.1.2 — the AdRoll consent launcher is a link whose only text sits in
     a display:none span, so it never reaches the accessibility tree. It is the
     only way a visitor can revisit their privacy choices (see L3).
     ------------------------------------------------------------------------- */
  function nameConsentLauncher() {
    var candidates = $$(
      '[class*="adroll"] a,[id*="adroll"] a,[class*="consent"] a,[class*="cookie"] a,' +
      '[class*="adroll"] button,[id*="adroll"] button,[class*="consent"] button,[class*="cookie"] button'
    );
    candidates.forEach(function (el) {
      if (text(el) || el.getAttribute('aria-label')) return;
      el.setAttribute('aria-label', 'Cookie and privacy preferences');
    });
  }

  /* ------------------------------------------------------------------------ M1
     WCAG 1.3.1 / 2.4.1.
     The homepage exposed only banner/navigation/main/contentinfo, so there was
     nothing to jump between across roughly four minutes of content. Promote
     each section that has a heading into a named region.
     ------------------------------------------------------------------------- */
  function nameRegions() {
    $$('#MainContent .shopify-section').forEach(function (sec) {
      if (sec.getAttribute('aria-label') || sec.getAttribute('aria-labelledby')) return;
      if (sec.querySelector('[role="region"][aria-labelledby],section[aria-labelledby]')) return;
      var heading = sec.querySelector('h1,h2,[class*="heading"],[class*="title"]');
      var label = heading ? text(heading) : '';
      if (!label || label.length > 90) return;
      if (!heading.id) heading.id = 'a11y-region-' + Math.random().toString(36).slice(2, 9);
      sec.setAttribute('role', 'region');
      sec.setAttribute('aria-labelledby', heading.id);
    });
  }

  /* ------------------------------------------------------------------------ M3
     WCAG 4.1.3 — changing a variant updates the price silently.
     ------------------------------------------------------------------------- */
  function watchVariantChanges() {
    var priceEl = document.querySelector('[data-product-price],.product__price,.price__current,[class*="product-single__price"]');
    if (!priceEl || !once(priceEl, 'a11yPriceWatch')) return;
    var lastPrice = text(priceEl);
    new MutationObserver(function () {
      var now = text(priceEl);
      if (now && now !== lastPrice) {
        lastPrice = now;
        var selected = $$('input[type="radio"]:checked')
          .map(function (r) { return r.labels && r.labels[0] ? text(r.labels[0]) : ''; })
          .filter(Boolean).join(', ');
        announce((selected ? selected + '. ' : '') + 'Price ' + now);
      }
    }).observe(priceEl, { childList: true, characterData: true, subtree: true });

    // Announce the option itself even when the price does not move.
    $$('input[type="radio"]').forEach(function (r) {
      if (!once(r, 'a11yVariantWatch')) return;
      r.addEventListener('change', function () {
        if (!r.checked) return;
        var name = r.labels && r.labels[0] ? text(r.labels[0]) : r.value;
        var group = '';
        var fs = r.closest('fieldset');
        if (fs) {
          var legend = fs.querySelector('legend');
          if (legend) group = text(legend).replace(/\s*:\s*.*$/, '');
        }
        if (name) announce((group ? group + ': ' : '') + name + ' selected');
      });
    });
  }

  /* ------------------------------------------------------------------------ M4 / S10
     WCAG 4.1.3 — adding to cart produced about 1.5 seconds of silence, and the
     cart drawer has no live region, so quantity and removal changes say nothing.
     ------------------------------------------------------------------------- */
  function watchCartChanges() {
    if (watchCartChanges._wired) return;
    watchCartChanges._wired = true;

    function describe(cart) {
      if (!cart || typeof cart.item_count !== 'number') return '';
      if (cart.item_count === 0) return 'Your cart is empty';
      return 'Cart updated. ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ' in your cart.';
    }

    // Shopify's own cart events, where the theme or an app emits them.
    ['cart:updated', 'cart:refresh', 'rebuy:cart.change'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var cart = e && e.detail && (e.detail.cart || e.detail);
        var msg = describe(cart);
        if (msg) announce(msg);
      });
    });

    // Fallback: watch the cart-count bubble, which every path updates.
    var bubble = document.querySelector('.cart-link__bubble-num,[data-cart-count],.cart-count');
    if (bubble) {
      var last = text(bubble);
      new MutationObserver(function () {
        var now = text(bubble);
        if (now === last) return;
        var wasEmpty = !last || last === '0';
        last = now;
        if (!now || now === '0') { announce('Your cart is empty'); return; }
        announce('Cart updated. ' + now + (now === '1' ? ' item' : ' items') + ' in your cart.');
        if (wasEmpty) { /* first add — message above already covers it */ }
      }).observe(bubble, { childList: true, characterData: true, subtree: true });
    }

    // Intercept the cart AJAX endpoints so add/change/remove always announce,
    // including inside the Rebuy flyout which emits no DOM-visible signal.
    var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    var nativeFetch = window.fetch;

    // /cart/add.js answers with the line item that was added, not the cart, so
    // it carries no item_count. Read the cart back to report a real total.
    function announceCartAfterWrite(payload) {
      if (payload && typeof payload.item_count === 'number') {
        var direct = describe(payload);
        if (direct) announce(direct);
        return;
      }
      if (!nativeFetch) return;
      nativeFetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var msg = describe(cart);
          if (msg) announce(msg);
        })
        .catch(function () {});
    }

    if (nativeFetch && !window.fetch.__a11yWrapped) {
      var wrapped = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        return nativeFetch.apply(this, arguments).then(function (res) {
          if (CART_WRITE.test(url) && res && res.ok) {
            res.clone().json().then(announceCartAfterWrite).catch(function () {
              announceCartAfterWrite(null);
            });
          }
          return res;
        });
      };
      wrapped.__a11yWrapped = true;
      window.fetch = wrapped;
    }

    // Several installed apps still post the cart over XMLHttpRequest.
    if (window.XMLHttpRequest && !XMLHttpRequest.prototype.__a11yWrapped) {
      var nativeOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__a11yUrl = url;
        return nativeOpen.apply(this, arguments);
      };
      var nativeSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        xhr.addEventListener('load', function () {
          if (!CART_WRITE.test(xhr.__a11yUrl || '')) return;
          if (xhr.status < 200 || xhr.status >= 300) return;
          var data = null;
          try { data = JSON.parse(xhr.responseText); } catch (e) {}
          announceCartAfterWrite(data);
        });
        return nativeSend.apply(this, arguments);
      };
      XMLHttpRequest.prototype.__a11yWrapped = true;
    }
  }

  /* ------------------------------------------------------------------------ S10
     The theme's own cart drawer builds quantity steppers as <span> elements
     with click handlers, so they cannot be reached or operated by keyboard.
     Promote them to real buttons in place, preserving the existing handlers.
     ------------------------------------------------------------------------- */
  function fixQuantitySteppers() {
    $$('#CartDrawer span,#CartDrawer div,.rebuy-cart__flyout span').forEach(function (el) {
      if (el.querySelector('*')) return;
      var t = text(el);
      if (t !== '+' && t !== '-' && t !== '−') return;
      if (el.matches('button,[role="button"],[tabindex]:not([tabindex="-1"])')) return;
      if (!once(el, 'a11yStepper')) return;
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.classList.add('a11y-qty-stepper');
      el.setAttribute('aria-label', (t === '+' ? 'Increase quantity' : 'Decrease quantity'));
      el.addEventListener('keydown', function (evt) {
        if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Spacebar') {
          evt.preventDefault();
          el.click();
        }
      });
    });
  }

  /* ------------------------------------------------------------------------ S10
     Focus is dropped to <body> when the drawer re-renders after a removal.
     Put it somewhere sensible inside the dialog instead.
     ------------------------------------------------------------------------- */
  function guardDrawerFocus() {
    if (guardDrawerFocus._wired) return;
    guardDrawerFocus._wired = true;
    document.addEventListener('click', function (evt) {
      var remove = evt.target.closest && evt.target.closest(
        '#CartDrawer [href*="/cart/change"],#CartDrawer [data-cart-remove],' +
        '.rebuy-cart__flyout [class*="remove"],#CartDrawer [class*="remove"]'
      );
      if (!remove) return;
      var dialog = remove.closest('#CartDrawer,.rebuy-cart__flyout');
      if (!dialog) return;
      window.setTimeout(function () {
        if (document.activeElement && document.activeElement !== document.body &&
            dialog.contains(document.activeElement)) return;
        var next = dialog.querySelector('[class*="remove"]') ||
                   dialog.querySelector('[class*="close"]') ||
                   dialog.querySelector(FOCUSABLE);
        if (next && next.focus) next.focus();
      }, 400);
    }, true);
  }

  /* ---------------------------------------------------------------------- run */
  function runAll() {
    try { initMotionToggle(); } catch (e) {}
    try { syncExpanded(); } catch (e) {}
    try { fixSilentTabStops(); } catch (e) {}
    try { markDialogs(); } catch (e) {}
    try { demoteInjectedHeadings(); } catch (e) {}
    try { dedupeSkipLinks(); } catch (e) {}
    try { hideMenuChevrons(); } catch (e) {}
    try { wireSkipLink(); } catch (e) {}
    try { nameVagueLinks(); } catch (e) {}
    try { titleIframes(); } catch (e) {}
    try { nameConsentLauncher(); } catch (e) {}
    try { nameRegions(); } catch (e) {}
    try { watchVariantChanges(); } catch (e) {}
    try { watchCartChanges(); } catch (e) {}
    try { fixQuantitySteppers(); } catch (e) {}
    try { guardDrawerFocus(); } catch (e) {}
  }

  function start() {
    runAll();
    // Apps inject their markup late and re-render on interaction, so keep
    // re-applying, but cheaply: at most one pass every 750 ms, run when the
    // browser is idle, and never in response to our own DOM writes (the
    // records our pass produced are discarded before re-arming).
    var pending = null;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        var pass = function () {
          try { runAll(); } finally {
            observer.takeRecords();
            pending = null;
          }
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(pass, { timeout: 1500 });
        else pass();
      }, 750);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Dialog open/close is driven by class changes, so poll that, but only
    // once a second and only while the tab is visible.
    window.setInterval(function () {
      if (document.hidden) return;
      try { syncDialogInertness(); } catch (e) {}
      try { syncExpanded(); } catch (e) {}
    }, 1000);

    ['drawerOpen', 'drawerClose'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        window.setTimeout(function () {
          try { markDialogs(); syncDialogInertness(); fixQuantitySteppers(); } catch (e) {}
        }, 50);
      });
    });
  }

  // The fixes patch app markup that arrives late anyway, so the first pass
  // waits for an idle moment instead of competing with first paint.
  function boot() {
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
    else window.setTimeout(start, 1);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
